import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import {
  createLiveTasksFromBoq,
  getTaskTemplates,
  getUnitsByProjectId,
  syncBomWithTask
} from '../../services/api/implementation/impl';
import { getApiErrorMessage, mapUnitsForBoqTaskCreation } from '../../utils/boqBomExtract';

const SELECT_CLASS =
  'w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2.5 text-[14px] text-[#111827] focus:border-[#2A6A73] focus:outline-none focus:ring-1 focus:ring-[#2A6A73]';

/**
 * Creates live tasks from a generated BOQ: per unit (independent) or per block (group),
 * from a chosen task template set, optionally limited to the BOQ's work types. Each task
 * gets its share of the BOQ quantity; BOM materials can be attached in the same step.
 */
export const CreateTasksFromBoqModal = ({
  isOpen,
  kind,
  projectId,
  accommodationId,
  accommodationName,
  boqId,
  boqWorkTypeIds = [],
  canAttachBom = false,
  buildBomSyncPayload,
  onClose
}) => {
  const isGroup = kind === 'GroupUnit';
  const [units, setUnits] = useState([]);
  const [templateNames, setTemplateNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [onlyBoqWorkTypes, setOnlyBoqWorkTypes] = useState(true);
  const [attachBom, setAttachBom] = useState(canAttachBom);
  const [isCreating, setIsCreating] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setTemplateName('');
      setResults(null);
      setLoadError(null);
      return;
    }
    setAttachBom(canAttachBom);
    if (!projectId || !accommodationId) {
      setLoadError('Project and unit type are required.');
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      getUnitsByProjectId(projectId),
      getTaskTemplates(projectId, isGroup ? 'group' : 'independent')
    ])
      .then(([unitData, templates]) => {
        if (cancelled) return;
        setUnits(mapUnitsForBoqTaskCreation(unitData, accommodationId));
        const names = [...new Set((templates || []).map((t) => t.name).filter(Boolean))];
        setTemplateNames(names);
        setTemplateName(names[0] ?? '');
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load units or task templates.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, accommodationId, isGroup, canAttachBom]);

  /** Independent: one target per unit. Group: one per block (tasks are created block-wide). */
  const targets = useMemo(() => {
    if (!isGroup) return units.map((u) => ({ id: u.id, label: u.label, unitId: u.id }));
    const byBlock = new Map();
    units.forEach((u) => {
      if (u.blockId == null || byBlock.has(u.blockId)) return;
      byBlock.set(u.blockId, { id: u.blockId, label: u.blockLabel, unitId: u.id });
    });
    return [...byBlock.values()];
  }, [units, isGroup]);

  if (!isOpen) return null;

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleCreate = async () => {
    setIsCreating(true);
    const outcome = [];
    for (const target of targets.filter((t) => selectedIds.includes(t.id))) {
      const payload = {
        name: templateName,
        boq_id: boqId,
        ...(onlyBoqWorkTypes && boqWorkTypeIds.length ? { work_type_ids: boqWorkTypeIds } : {}),
        ...(isGroup ? { block_id: target.id, sections: 1 } : { unit_id: target.id })
      };
      try {
        const response = await createLiveTasksFromBoq(isGroup ? 'group' : 'independent', payload);
        const row = {
          label: target.label,
          ok: true,
          taskCount: Array.isArray(response?.data) ? response.data.length : 0,
          skippedWorkTypes: response?.meta?.boq?.workTypesWithoutTasks ?? []
        };
        if (attachBom && buildBomSyncPayload) {
          const syncPayload = buildBomSyncPayload(target.unitId);
          try {
            if (syncPayload) await syncBomWithTask(syncPayload);
            row.bomAttached = Boolean(syncPayload);
          } catch (err) {
            row.bomError = getApiErrorMessage(err, 'BOM sync failed');
          }
        }
        outcome.push(row);
      } catch (err) {
        outcome.push({
          label: target.label,
          ok: false,
          error: getApiErrorMessage(err, 'Failed to create tasks')
        });
      }
    }
    setResults(outcome);
    setIsCreating(false);
  };

  const targetNoun = isGroup ? 'block' : 'unit';
  const canSubmit =
    !isCreating && !isLoading && templateName && selectedIds.length > 0 && !loadError;

  return (
    <Modal name="Create tasks from BOQ" width={40} onClose={isCreating ? () => {} : onClose}>
      {results ? (
        <div className="space-y-3">
          {results.map((row) => (
            <div
              key={row.label}
              className="rounded-md border border-[#E5E7EB] px-3 py-2.5 text-[13px]"
            >
              <p className={`font-semibold ${row.ok ? 'text-[#166534]' : 'text-[#B91C1C]'}`}>
                {row.label}: {row.ok ? `${row.taskCount} tasks created` : row.error}
              </p>
              {row.ok && row.skippedWorkTypes.length > 0 && (
                <p className="mt-1 text-[#B45309]">
                  No template task for: {row.skippedWorkTypes.join(', ')}
                </p>
              )}
              {row.bomAttached && <p className="mt-1 text-[#4B5563]">BOM materials attached</p>}
              {row.bomError && (
                <p className="mt-1 text-[#B45309]">BOM not attached: {row.bomError}</p>
              )}
            </div>
          ))}
          <p className="text-[12px] text-[#6B7280]">
            Tasks are created in preview. Set their dates from the live tasks page. BOQ quantities
            can be edited on each task.
          </p>
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-[#2A6A73] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#245a62]"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-[14px] text-[#4B5563] leading-relaxed">
            Create live tasks for{' '}
            <span className="font-semibold text-[#111827]">{accommodationName}</span> from this BOQ.
            Each task gets its share of the BOQ quantity for its work type.
          </p>

          {isLoading ? (
            <p className="text-[13px] text-[#6B7280]">Loading…</p>
          ) : loadError ? (
            <p className="text-[13px] text-[#B45309]">{loadError}</p>
          ) : (
            <>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                  Task template
                </label>
                {templateNames.length ? (
                  <select
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    {templateNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-[13px] text-[#B45309]">
                    No {isGroup ? 'group' : 'independent'} task templates in this project.
                  </p>
                )}
              </div>

              <div>
                <p className="block text-[13px] font-medium text-[#374151] mb-1.5">Which tasks</p>
                <label className="flex items-start gap-2 text-[13px] text-[#374151] mb-1.5">
                  <input
                    type="radio"
                    checked={onlyBoqWorkTypes}
                    onChange={() => setOnlyBoqWorkTypes(true)}
                    className="mt-0.5"
                  />
                  Only work types in this BOQ ({boqWorkTypeIds.length})
                </label>
                <label className="flex items-start gap-2 text-[13px] text-[#374151]">
                  <input
                    type="radio"
                    checked={!onlyBoqWorkTypes}
                    onChange={() => setOnlyBoqWorkTypes(false)}
                    className="mt-0.5"
                  />
                  All template tasks (BOQ quantity filled where the BOQ has that work type)
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[13px] font-medium text-[#374151]">
                    {isGroup ? 'Blocks' : 'Units'} ({selectedIds.length} selected)
                  </p>
                  {targets.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedIds(
                          selectedIds.length === targets.length ? [] : targets.map((t) => t.id)
                        )
                      }
                      className="text-[12px] font-medium text-[#2A6A73] hover:underline"
                    >
                      {selectedIds.length === targets.length ? 'Clear' : 'Select all'}
                    </button>
                  )}
                </div>
                {targets.length ? (
                  <div className="max-h-48 overflow-y-auto rounded-md border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                    {targets.map((target) => (
                      <label
                        key={target.id}
                        className="flex items-center gap-2 px-3 py-2 text-[13px] text-[#111827] hover:bg-[#F9FAFB]"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(target.id)}
                          onChange={() => toggle(target.id)}
                        />
                        {target.label}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[13px] text-[#B45309]">
                    No {targetNoun}s are linked to this unit type.
                  </p>
                )}
                <p className="mt-1 text-[12px] text-[#6B7280]">
                  A {targetNoun} that already has tasks is skipped with an error.
                </p>
              </div>

              <label
                className={`flex items-start gap-2 text-[13px] ${
                  canAttachBom ? 'text-[#374151]' : 'text-[#9CA3AF]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={attachBom}
                  disabled={!canAttachBom}
                  onChange={(e) => setAttachBom(e.target.checked)}
                  className="mt-0.5"
                />
                Attach BOM materials to the new tasks
                {!canAttachBom && ' (create the BOM and apply costing first)'}
              </label>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="rounded-md border border-[#D1D5DB] bg-white px-4 py-2 text-[13px] font-medium text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!canSubmit}
              className="rounded-md bg-[#2A6A73] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#245a62] disabled:opacity-50"
            >
              {isCreating ? 'Creating tasks…' : 'Create tasks'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreateTasksFromBoqModal;
